import { useEffect, useRef, useState } from 'react';

import { LIST_BUCKET, boatsRpcArgs } from '../list-queries';
import type { Boat } from '../boats';
import { getPrefetched } from '../static-prefetch';
import { supabase } from '../supabase';

export type BoatsQuery = {
  city?: string;
  limit?: number;
};

/**
 * Mirrors use-tours.ts: calls the `boats_by_city` RPC. Null city → no filter.
 *
 * `seedKey` opts into the build-time seed so /[lang]/boats ships its cards as
 * real links in the static HTML instead of "Loading boats…" — see
 * the app's src/lib/detail/list-prefetch.ts.
 */
export function useBoats(query: BoatsQuery = {}, seedKey?: string) {
  const [seed] = useState(() => getPrefetched<Boat[]>(LIST_BUCKET, seedKey));
  const [boats, setBoats] = useState<Boat[]>(seed ?? []);
  const [loading, setLoading] = useState(seed === undefined);
  const [error, setError] = useState<string | null>(null);

  const key = JSON.stringify(query);
  const seededKey = useRef(seed !== undefined ? key : null);

  useEffect(() => {
    let cancelled = false;
    const isSeededFirstRun = seededKey.current === key;
    seededKey.current = null;

    if (isSeededFirstRun) return;

    setLoading(true);
    setError(null);

    supabase
      .rpc('boats_by_city', boatsRpcArgs(query))
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          setError(error.message);
          setBoats([]);
        } else {
          setBoats((data ?? []) as Boat[]);
        }
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return { boats, loading, error };
}
